import FeedbackIcon from '@mui/icons-material/Feedback';
import UndoIcon from '@mui/icons-material/Undo';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface HeapActivityActionsProps {
  onUndoInvalidation?: () => void;
  invalidDetectionIndexes?: number[];
  onSubmitComplaint: (text: string) => Promise<void>;
  isSubmitComplaintPending?: boolean;
}

export function HeapActivityActions({
  onUndoInvalidation,
  invalidDetectionIndexes,
  onSubmitComplaint,
  isSubmitComplaintPending = false,
}: HeapActivityActionsProps): ReactElement {
  const { t } = useTranslation();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [reportText, setReportText] = useState('');

  const undoDisabled = onUndoInvalidation === undefined || (invalidDetectionIndexes?.length ?? 0) === 0;

  const handleOpenDialog = () => {
    setReportText('');
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (isSubmitComplaintPending) {
      return;
    }
    setIsDialogOpen(false);
  };

  const handleSubmit = async () => {
    try {
      await onSubmitComplaint(reportText);
      handleCloseDialog();
    } catch {
      // keep dialog open so the user can retry or edit the complaint text
    }
  };

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: '1px solid #e0e0e0',
        backgroundColor: '#f5f5f5',
        minHeight: '4vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1,
        gap: 1,
      }}
    >
      <Tooltip title={t('heap.complaint.title')}>
        <Button
          variant='text'
          startIcon={<FeedbackIcon />}
          onClick={handleOpenDialog}
          disabled={isSubmitComplaintPending}
        >
          {t('heap.complaint.title')}
        </Button>
      </Tooltip>
      <Tooltip title={t('common.undo')}>
        <IconButton onClick={onUndoInvalidation} disabled={undoDisabled}>
          <UndoIcon />
        </IconButton>
      </Tooltip>

      <Dialog open={isDialogOpen} onClose={handleCloseDialog} fullWidth maxWidth='sm'>
        <DialogTitle>{t('heap.complaint.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('heap.complaint.reportInfo')}</DialogContentText>
          <TextField
            autoFocus
            margin='dense'
            type='text'
            fullWidth
            multiline
            rows={4}
            variant='outlined'
            value={reportText}
            onChange={(e) => setReportText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isSubmitComplaintPending}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => void handleSubmit()}
            variant='contained'
            disabled={isSubmitComplaintPending}
            startIcon={isSubmitComplaintPending ? <CircularProgress size={16} color='inherit' /> : undefined}
          >
            {t('common.submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
